import { DashboardState, ESdChangeReason } from "@kuika/kuika-cl-model";
import { IBranchDto } from "@kuika/kuika-cl-model/src/dtos/branch.dto";
import BranchingService from "../../../../../services/branching-service";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../../shared/hoc/with-history";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import { sortBranchesMasterFirst } from "../../../../../shared/utility/branch-helpers";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import KuikaBadge from "../../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton, { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaMenuItem from "../../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaSpinner from "../../../../ui-kit/kuika-spinner/kuika-spinner";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { type ToastId, toastService } from "../../../../ui-kit/kuika-toast/toast-service";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaTextMedium from "../../../../ui-kit/typography/TextMedium/TextMedium";
import {
  BRANCH_NAME_MAX_LENGTH,
  IBranchNameValidationResult,
  validateBranchName
} from "../../../branching/branch-name-validation";
import CreateBranchModal from "../../../branching/create-branch-modal";
import CreateBranchUncommittedModal from "../../../branching/create-branch-uncommitted-modal";
import DeleteBranchModal from "../../../branching/delete-branch-modal";
import RenameBranchModal from "../../../branching/rename-branch-modal";
import SwitchBranchWarningModal from "../../../branching/switch-branch-warning-modal";
import "./branching-panel.scss";

/** What the switch's toast says when the branch could not be switched to. */
const describeSwitchFailure = (reason: unknown): string => {
  if (reason instanceof Error && reason.message) return reason.message;
  return "The branch could not be switched.";
};

export interface IBranchingPanelState {
  searchText: string;
  isLoading: boolean;
  selectedBranch: IBranchDto | null;
  showCreateBranchModal: boolean;
  showRenameBranchModal: boolean;
  newBranchName: string;
  selectedSourceBranch: string;
  branchToRename: IBranchDto | null;
  renameBranchName: string;
  inProgress: boolean;
  showSwitchWarning: boolean;
  pendingSwitchBranch: IBranchDto | null;
  switchStagedCount: number;
  showDeleteModal: boolean;
  branchToDelete: IBranchDto | null;
  showUncommittedModal: boolean;
  uncommittedCount: number;
  uncommittedCommitMessage: string;
  /**
   * Where the open dialog's commit stands — create, rename or delete; only one of them is ever
   * open. Owned by `SaveStatus`, never set directly.
   */
  commitStatus: KuikaButtonStatus;
  /** Which of the uncommitted-changes dialog's two buttons ran the create, so it carries the status. */
  uncommittedAction: "commit" | "without";
}

export interface IBranchingPanelProps {}

class BranchingPanel extends PureSdComponent<
  IBranchingPanelProps & IWithSDProps & IWithContextProp & IWithHistoryProp,
  IBranchingPanelState
> {
  constructor(props: IBranchingPanelProps & IWithSDProps & IWithContextProp & IWithHistoryProp) {
    super(props);
    this.state = {
      searchText: "",
      isLoading: false,
      selectedBranch: null,
      showCreateBranchModal: false,
      showRenameBranchModal: false,
      newBranchName: "",
      selectedSourceBranch: "",
      branchToRename: null,
      renameBranchName: "",
      inProgress: false,
      showSwitchWarning: false,
      pendingSwitchBranch: null,
      switchStagedCount: 0,
      showDeleteModal: false,
      branchToDelete: null,
      showUncommittedModal: false,
      uncommittedCount: 0,
      uncommittedCommitMessage: "",
      commitStatus: "idle",
      uncommittedAction: "commit"
    };
  }

  /**
   * The dialogs' commit state machine. Create and rename hold their ✓ and then close onto the
   * list, which shows the result; delete goes back to idle as it closes, its outcome a toast.
   */
  private readonly commitStatus: SaveStatus = new SaveStatus((commitStatus: KuikaButtonStatus): void => {
    this.setState({ commitStatus });
  });

  componentWillUnmount(): void {
    this.commitStatus.dispose();
  }

  componentDidMount = async () => {
    this.setState({
      selectedBranch: this.props.sd.currentBranch || {
        id: "",
        name: "master"
      }
    });
  };

  selectBranch = async (branchId: string) => {
    const currentBranchId = this.props.sd.currentBranch?.id;
    if (branchId === currentBranchId) return;

    // Switching to a protected branch is allowed — it opens view-only (banner + commit guard).

    // Guard against silently losing uncommitted work — warn before switching away
    // from a branch that still has staged (uncommitted) changes.
    if (currentBranchId) {
      try {
        const stagedRes = await BranchingService.GetStagedEntities(currentBranchId);
        const stagedCount = stagedRes?.data?.length ?? 0;
        if (stagedCount > 0) {
          const targetBranch = this.props.sd.branches?.find((branch) => branch.id === branchId) ?? null;
          this.setState({
            showSwitchWarning: true,
            pendingSwitchBranch: targetBranch,
            switchStagedCount: stagedCount
          });
          return;
        }
      } catch {
        // If the staged check fails, fall through and switch (don't block the user).
      }
    }

    this.performBranchSwitch(branchId);
  };

  /**
   * A switch reloads the project under the other branch — a context change, so the full-screen
   * spinner stays — and one toast carries it from "Switching" to its outcome. The spinner used to
   * stay up for good when the switch was refused; it now clears whichever way the switch ends.
   */
  private performBranchSwitch = (branchId: string): void => {
    const branch = this.props.sd.branches?.find((b) => b.id === branchId);
    this.setState({
      selectedBranch: branch || { id: "", name: "master" },
      isLoading: true,
      showSwitchWarning: false,
      pendingSwitchBranch: null
    });
    let toastId: ToastId | null = null;
    const work: Promise<void> = (async (): Promise<void> => {
      let isSwitched: boolean;
      try {
        const res = await BranchingService.UpdateUserBranchingState(branchId);
        isSwitched = !!res.data;
      } catch (error: unknown) {
        // The service has already reported the failure in its own toast; this one steps aside
        // before the rejection reaches it, rather than say the same thing twice.
        if (toastId !== null) toastService.dismiss(toastId);
        throw error;
      }
      if (!isSwitched) {
        throw new Error(`Could not switch to ${branch?.name ?? "the branch"}.`);
      }
      this.props.sd.setCurrentBranch(branchId);
    })();
    toastId = toastService.promise(work, {
      loading: `Switching to ${branch?.name ?? "the branch"}…`,
      success: `Switched to branch: ${branch?.name}. Reloading project. Please wait...`,
      error: describeSwitchFailure
    });
    void work
      .catch((): void => undefined)
      .finally((): void => {
        this.setState({ isLoading: false });
      });
  };

  handleConfirmSwitch = () => {
    const branchId = this.state.pendingSwitchBranch?.id;
    if (branchId) this.performBranchSwitch(branchId);
  };

  handleCancelSwitch = () => {
    this.setState({ showSwitchWarning: false, pendingSwitchBranch: null });
  };

  getFilteredBranches = (searchText: string) =>
    sortBranchesMasterFirst(
      this.props.sd.branches.filter((branch) => branch?.name?.toLowerCase().includes(searchText.toLowerCase()))
    );

  handleCreateNewBranch = () => {
    this.setState({
      showCreateBranchModal: true,
      newBranchName: "",
      selectedSourceBranch: this.props.sd.currentBranch?.id || ""
    });
  };

  handleCreateNewBranchFrom = (branch: IBranchDto) => {
    const sanitizedSource: string = (branch.name ?? "").replace(/[^A-Za-z0-9]/g, "");
    const suggestedRaw: string = `${sanitizedSource}copy`;
    const suggested: string = suggestedRaw.slice(0, BRANCH_NAME_MAX_LENGTH);
    this.setState({
      showCreateBranchModal: true,
      newBranchName: suggested,
      selectedSourceBranch: branch.id
    });
  };

  handleRenameBranch = (branch: IBranchDto) => {
    this.setState({
      showRenameBranchModal: true,
      branchToRename: branch,
      renameBranchName: branch.name
    });
  };

  handleMergeBranch = async (branch: IBranchDto) => {
    try {
      if (branch?.id && branch.id !== this.props.sd.currentBranch?.id) {
        const res = await BranchingService.UpdateUserBranchingState(branch.id);
        if (res?.data) {
          this.props.sd.setCurrentBranch(branch.id);
        }
      }

      BranchingService.RequestStartMerge();
      this.props.sd.setDashboardState(DashboardState.branching);
    } catch (error) {
      KMainFunctions.displayErrorNotification("Failed to open merge flow");
    }
  };

  handleDeleteBranch = (branch: IBranchDto) => {
    this.setState({ showDeleteModal: true, branchToDelete: branch });
  };

  handleCloseDeleteModal = () => {
    if (this.state.inProgress || !this.commitStatus.canStart) return;
    this.setState({ showDeleteModal: false, branchToDelete: null });
  };

  handleConfirmDeleteBranch = async () => {
    const branch = this.state.branchToDelete;
    if (!branch || !this.commitStatus.canStart) return;
    this.commitStatus.start();
    this.setState({ inProgress: true });
    try {
      await this.props.sd.deleteBranch(branch.id);
      if (this.props.sd.currentBranch?.id === branch.id) {
        const masterBranch = this.props.sd.branches?.find((b) => b.name?.toLowerCase() === "master");
        if (masterBranch) {
          await BranchingService.UpdateUserBranchingState(masterBranch.id);
          await this.props.sd.setCurrentBranch(masterBranch.id);
        }
      }
      // The branch and the dialog are gone: the toast says so, not a ✓.
      this.commitStatus.reset();
      KMainFunctions.displaySuccessNotification(`Branch "${branch.name}" has been deleted.`);
      this.setState({ showDeleteModal: false, branchToDelete: null });
    } catch (error) {
      this.commitStatus.fail();
      KMainFunctions.displayErrorNotification("Failed to delete branch.");
    } finally {
      this.setState({ inProgress: false });
    }
  };

  handleCreateBranchSubmit = async () => {
    const { newBranchName, selectedSourceBranch } = this.state;

    const existingNames: string[] = (this.props.sd.branches ?? []).map((branch: IBranchDto) => branch.name ?? "");
    const validation: IBranchNameValidationResult = validateBranchName(newBranchName, { existingNames });
    if (!validation.valid) {
      KMainFunctions.displayErrorNotification(validation.error ?? "Invalid branch name.");
      return;
    }
    if (!this.commitStatus.canStart) return;

    this.commitStatus.start();
    this.setState({ inProgress: true });

    try {
      const stagedRes = await BranchingService.GetStagedEntities(selectedSourceBranch);
      const stagedCount = stagedRes?.data?.length ?? 0;

      if (stagedCount > 0) {
        // Not a failure: the create waits on a second dialog that asks about the changes.
        this.commitStatus.reset();
        this.setState({
          inProgress: false,
          showUncommittedModal: true,
          uncommittedCount: stagedCount,
          uncommittedCommitMessage: ""
        });
        return;
      }

      await this.doCreateBranch();
    } catch (error) {
      this.commitStatus.fail();
      KMainFunctions.displayErrorNotification("Failed to create branch");
      this.setState({ inProgress: false });
    }
  };

  handleUncommittedCommitAndCreate = async () => {
    const message = this.state.uncommittedCommitMessage;
    if (!message.trim() || !this.commitStatus.canStart) {
      if (!message.trim()) KMainFunctions.displayErrorNotification("Please provide a commit message.");
      return;
    }
    this.setState({ uncommittedAction: "commit" });
    await this.commitAndCreateBranch(message);
  };

  handleUncommittedCreateWithout = async () => {
    if (!this.commitStatus.canStart) return;
    this.setState({ uncommittedAction: "without" });
    await this.doCreateBranch();
  };

  handleCloseUncommittedModal = () => {
    if (this.state.inProgress || !this.commitStatus.canStart) return;
    this.setState({ showUncommittedModal: false });
  };

  /** The ✓ is held on the dialog, then it closes onto the list, where the new branch now stands. */
  private doCreateBranch = async () => {
    const { newBranchName, selectedSourceBranch } = this.state;
    this.commitStatus.start();
    this.setState({ inProgress: true });
    try {
      await this.props.sd.createBranch({
        name: newBranchName,
        parentBranchId: selectedSourceBranch
      });
      this.commitStatus.succeed((): void => {
        this.setState({
          showCreateBranchModal: false,
          showUncommittedModal: false,
          newBranchName: "",
          selectedSourceBranch: "",
          uncommittedCommitMessage: ""
        });
      });
    } catch (error) {
      this.commitStatus.fail();
      KMainFunctions.displayErrorNotification("Failed to create branch");
    } finally {
      this.setState({ inProgress: false });
    }
  };

  private commitAndCreateBranch = async (commitMessage: string) => {
    const { selectedSourceBranch } = this.state;
    this.commitStatus.start();
    this.setState({ inProgress: true });
    try {
      await BranchingService.CommitStaged(selectedSourceBranch, commitMessage);
      this.props.sd.loadScreen(this.props.sd.sdId, true, true);
      await this.doCreateBranch();
    } catch (error) {
      this.commitStatus.fail();
      KMainFunctions.displayErrorNotification("Failed to commit and create branch");
      this.setState({ inProgress: false });
    }
  };

  handleRenameBranchSubmit = async () => {
    const { branchToRename, renameBranchName } = this.state;

    if (!branchToRename) return;

    const existingNames: string[] = (this.props.sd.branches ?? []).map((branch: IBranchDto) => branch.name ?? "");
    const validation: IBranchNameValidationResult = validateBranchName(renameBranchName, {
      existingNames,
      currentName: branchToRename.name
    });
    if (!validation.valid) {
      KMainFunctions.displayErrorNotification(validation.error ?? "Invalid branch name.");
      return;
    }
    if (!this.commitStatus.canStart) return;

    this.commitStatus.start();
    this.setState({ inProgress: true });

    try {
      await this.props.sd.renameBranch(branchToRename.id, renameBranchName);
      await this.props.sd.getBranches();
      // The ✓ first; the list carries the new name once the dialog has gone.
      this.commitStatus.succeed((): void => {
        this.setState({ showRenameBranchModal: false, branchToRename: null, renameBranchName: "" });
      });
    } catch (error) {
      this.commitStatus.fail();
      KMainFunctions.displayErrorNotification("Failed to rename branch");
    } finally {
      this.setState({ inProgress: false });
    }
  };

  handleToggleProtection = async (branch: IBranchDto) => {
    const next = !branch.isProtected;
    try {
      const res = await BranchingService.SetBranchProtection(branch.id, next);
      if (res?.data) {
        KMainFunctions.displaySuccessNotification(
          next ? `“${branch.name}” is now protected.` : `“${branch.name}” is no longer protected.`
        );
        await this.props.sd.getBranches();
      }
    } catch {
      KMainFunctions.displayErrorNotification("Failed to update branch protection.");
    }
  };

  branchMenu = (branch: IBranchDto) => {
    // Permission flags come from the server (single source of truth); undefined = fail open, since
    // the backend still enforces every action independently.
    const perms = branch.permissions;
    const isMaster = branch.name?.toLowerCase() === "master";
    return [
      {
        key: "rename",
        label: "Rename branch",
        icon: <KuikaSvg width={16} height={16} name="edit" />,
        onClick: () => this.handleRenameBranch(branch),
        hidden: isMaster || perms?.canRename === false
      },
      {
        key: "create-from",
        label: "Create new from this branch",
        icon: <KuikaSvg width={16} height={16} name="copy" />,
        onClick: () => this.handleCreateNewBranchFrom(branch),
        hidden: perms?.canCreateBranch === false
      },
      {
        key: "protection",
        label: branch.isProtected ? "Unprotect branch" : "Protect branch",
        icon: <KuikaSvg width={16} height={16} name="lock" />,
        onClick: () => this.handleToggleProtection(branch),
        hidden: perms?.canToggleProtection !== true
      },
      {
        key: "merge",
        label: "Merge into another branch",
        icon: <KuikaSvg width={16} height={16} name="git-merge" />,
        onClick: () => this.handleMergeBranch(branch),
        group: 1
      },
      {
        key: "delete",
        label: "Delete branch",
        icon: <KuikaSvg width={16} height={16} name="trash" />,
        onClick: () => this.handleDeleteBranch(branch),
        danger: true,
        group: 2,
        hidden: isMaster || perms?.canDelete === false
      }
    ];
  };

  handleSearch = (searchText: string) => {
    this.setState({ searchText });
  };

  render() {
    const {
      searchText,
      selectedBranch,
      showCreateBranchModal,
      showRenameBranchModal,
      newBranchName,
      selectedSourceBranch,
      renameBranchName,
      inProgress
    } = this.state;
    const filteredBranches = this.getFilteredBranches(searchText);
    const branchCount = this.props.sd.branches?.length;
    const existingBranchNames: string[] = (this.props.sd.branches ?? []).map((branch: IBranchDto) => branch.name ?? "");
    // Project-level flag (identical across branches); undefined = fail open.
    const canCreateBranch: boolean =
      this.props.sd.currentBranch?.permissions?.canCreateBranch ??
      this.props.sd.branches?.[0]?.permissions?.canCreateBranch ??
      true;

    return (
      <>
        <KuikaSpinner size="large" spinning={this.state.isLoading} />
        <div className="kuika-branch-list">
          <div className="kuika-branch-list__header">
            <div className="kuika-branch-list__title">
              <KuikaTextMedium className="kuika-branch-list__title-text">Branch List</KuikaTextMedium>
              <KuikaBadge color="blue" content={branchCount} />
            </div>
            <KuikaTooltip content={canCreateBranch ? "New branch" : "You don't have permission to create branches"}>
              <KuikaButton
                isIcon
                type="tertiary"
                color="neutral"
                disabled={!canCreateBranch}
                onClick={this.handleCreateNewBranch}
              >
                <KuikaSvg width={16} height={16} name="plus" />
              </KuikaButton>
            </KuikaTooltip>
          </div>
          <div className="kuika-branch-list__search">
            <KuikaInput
              size="md"
              placeholder="Search branch..."
              value={this.state.searchText}
              onChange={(e) => this.handleSearch(e.target.value)}
              leftIcon={<KuikaSvg name="search" width={14} height={14} />}
            />
          </div>
          <div className="kuika-branch-list__items">
            {filteredBranches?.map((branch) => (
              <KuikaMenuItem
                key={branch?.id}
                title={branch?.name}
                titleSuffix={
                  branch?.isProtected ? (
                    <KuikaTooltip
                      content={
                        branch?.permissions?.canMergeInto
                          ? "Protected branch — you can commit here"
                          : "Protected branch — view-only for you; propose changes via a merge request"
                      }
                    >
                      <KuikaSvg
                        name={branch?.permissions?.canMergeInto ? "lock-open" : "lock"}
                        width={12}
                        height={12}
                      />
                    </KuikaTooltip>
                  ) : undefined
                }
                subtitle={branch?.createdBy?.name ? `by ${branch.createdBy.name}` : undefined}
                onClick={() => this.selectBranch(branch?.id)}
                selected={selectedBranch?.id === branch?.id}
                rightDropdown={
                  <KuikaDropdownMenu options={this.branchMenu(branch)} overlayClassName="kuika-screens__dropdown">
                    {(open) => (
                      <KuikaButton isIcon type="tertiary" color="neutral" active={open}>
                        <KuikaSvg width={16} height={16} name="dots" />
                      </KuikaButton>
                    )}
                  </KuikaDropdownMenu>
                }
              />
            ))}
          </div>
        </div>

        <CreateBranchModal
          showModal={showCreateBranchModal}
          branchName={newBranchName}
          selectedSourceBranch={selectedSourceBranch}
          branchOptions={sortBranchesMasterFirst(this.props.sd.branches).map((branch) => ({
            value: branch.id,
            label: branch.name
          }))}
          existingBranchNames={existingBranchNames}
          inProgress={inProgress}
          status={this.state.commitStatus}
          onClose={() => this.commitStatus.canStart && this.setState({ showCreateBranchModal: false })}
          onBranchNameChange={(e) => this.setState({ newBranchName: e.target.value })}
          onSourceBranchChange={(value) => this.setState({ selectedSourceBranch: value as string })}
          onCreateBranch={this.handleCreateBranchSubmit}
        />

        <RenameBranchModal
          showModal={showRenameBranchModal}
          branchName={renameBranchName}
          currentBranchName={this.state.branchToRename?.name ?? ""}
          existingBranchNames={existingBranchNames}
          status={this.state.commitStatus}
          onClose={() => this.commitStatus.canStart && this.setState({ showRenameBranchModal: false })}
          onBranchNameChange={(e) => this.setState({ renameBranchName: e.target.value })}
          onRenameBranch={this.handleRenameBranchSubmit}
        />

        <SwitchBranchWarningModal
          showModal={this.state.showSwitchWarning}
          targetBranchName={this.state.pendingSwitchBranch?.name}
          changeCount={this.state.switchStagedCount}
          inProgress={this.state.isLoading}
          onClose={this.handleCancelSwitch}
          onConfirm={this.handleConfirmSwitch}
        />

        <DeleteBranchModal
          showModal={this.state.showDeleteModal}
          branchName={this.state.branchToDelete?.name}
          inProgress={inProgress}
          status={this.state.commitStatus}
          onClose={this.handleCloseDeleteModal}
          onDelete={this.handleConfirmDeleteBranch}
        />

        <CreateBranchUncommittedModal
          showModal={this.state.showUncommittedModal}
          changeCount={this.state.uncommittedCount}
          commitMessage={this.state.uncommittedCommitMessage}
          inProgress={inProgress}
          status={this.state.commitStatus}
          statusAction={this.state.uncommittedAction}
          onClose={this.handleCloseUncommittedModal}
          onCommitMessageChange={(value) => this.setState({ uncommittedCommitMessage: value })}
          onCommitAndCreate={this.handleUncommittedCommitAndCreate}
          onCreateWithoutCommitting={this.handleUncommittedCreateWithout}
        />
      </>
    );
  }

  checkNeedRender = (_: ESdChangeReason): boolean => false;
}

const branchingPanel = withContext(withHistory(withSD(BranchingPanel)));
export { branchingPanel as BranchingPanel };
